import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { Device } from "@twilio/voice-sdk";
import { toast } from "sonner";
import { getVoiceToken, logVoiceCall, lookupCaller, checkVoiceConfig, reportCallError, getTwilioVoiceStatus } from "./api";

/**
 * Serialise a Twilio/JS error object into a flat payload that survives
 * JSON encoding. Twilio Voice errors carry interesting data on
 * non-enumerable fields (code, causes, solutions, explanation), so a
 * naive JSON.stringify drops them. Pull them out explicitly.
 */
function serialiseError(err) {
  if (!err) return { message: "Unknown error (null)" };
  if (typeof err === "string") return { message: err };
  return {
    name: err.name || err.constructor?.name || null,
    code: err.code !== undefined ? err.code : null,
    message: err.message || String(err),
    stack: err.stack || null,
    causes: Array.isArray(err.causes) ? err.causes : null,
    solutions: Array.isArray(err.solutions) ? err.solutions : null,
    explanation: err.explanation || null,
  };
}

/**
 * Format an error for the UI as a readable, multi-line string.
 */
function formatErrorForUi(err) {
  const s = serialiseError(err);
  const head = s.code !== null ? `[${s.code}] ${s.message}` : (s.message || "Call error");
  const causes = s.causes?.length ? `\nCauses: ${s.causes.join("; ")}` : "";
  const solutions = s.solutions?.length ? `\nSolutions: ${s.solutions.join("; ")}` : "";
  const explanation = s.explanation ? `\n${s.explanation}` : "";
  return `${head}${causes}${solutions}${explanation}`;
}

/**
 * Best-effort POST of a frontend voice error to the backend so it lands
 * in the server logs even if the user dismisses the toast or closes the
 * tab. Never throws — diagnostics must not break the calling flow.
 */
function reportToBackend(layer, err, extra = {}) {
  try {
    const payload = {
      layer,
      ...serialiseError(err),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      page_url: typeof window !== "undefined" ? window.location.href : null,
      ...extra,
    };
    // Fire-and-forget. Swallow any rejection.
    reportCallError(payload).catch(() => {});
  } catch {
    // Diagnostics must never break the call flow
  }
}

/** True when err.code is a Twilio signaling connection failure. */
function isConnectionError(err) {
  return err?.code === 31005 || err?.code === 31009;
}

const VoiceContext = createContext(null);

export function useVoice() {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error("useVoice must be used within VoiceProvider");
  return ctx;
}

/**
 * Global voice state provider. Manages a single Twilio Device instance
 * that persists across page navigations. Handles outbound and inbound calls.
 */
export function VoiceProvider({ children }) {
  const [status, setStatus] = useState("idle"); // idle | initializing | connecting | ringing | connected | disconnected | incoming
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callSid, setCallSid] = useState(null);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callerInfo, setCallerInfo] = useState(null); // { supplierId, enquiryId, supplierName }
  const [showDialer, setShowDialer] = useState(false);
  const [deviceReady, setDeviceReady] = useState(false);
  const [callDirection, setCallDirection] = useState("outbound"); // "inbound" | "outbound"
  // Kill-switch state mirrored from GET /api/voice/twilio-status. Starts
  // as null (unknown) so components render nothing until the first probe
  // returns. Flips to false when the backend reports the kill switch is
  // off OR the startup credential probe failed, hiding the call button
  // rather than leading the user into a 20101 AccessTokenInvalid error.
  const [voiceEnabled, setVoiceEnabled] = useState(null);
  const [voiceDisabledReason, setVoiceDisabledReason] = useState(null);

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const initializingRef = useRef(false);
  const notificationRef = useRef(null);
  const ringtoneRef = useRef(null);
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);
  // Ref to the latest initDevice so error-recovery handlers avoid stale closures.
  const initDeviceRef = useRef(null);
  // Interval ID for the fallback token refresh timer.
  const tokenRefreshIntervalRef = useRef(null);
  // Cooldown flag to prevent tight recovery loops when the backend is unreachable.
  const recoveryAttemptedRef = useRef(false);
  // When a 31005/31009 fires while the tab is backgrounded (Chrome suspends
  // network I/O), we cannot recover immediately. This flag defers recovery
  // until the visibilitychange event fires and the tab is foregrounded.
  const needsRecoveryRef = useRef(false);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    // Generate a simple ringtone using Web Audio API
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gainNode = ctx.createGain();
      gainNode.gain.value = 0.3;
      gainNode.connect(ctx.destination);

      let playing = true;
      const ring = () => {
        if (!playing) return;
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 440;
        osc.connect(gainNode);
        osc.start();
        osc.stop(ctx.currentTime + 0.4);
        setTimeout(() => {
          if (!playing) return;
          const osc2 = ctx.createOscillator();
          osc2.type = "sine";
          osc2.frequency.value = 480;
          osc2.connect(gainNode);
          osc2.start();
          osc2.stop(ctx.currentTime + 0.4);
        }, 500);
        if (playing) setTimeout(ring, 3000);
      };
      ring();

      // Store a mock audio object with pause to stop the ring
      ringtoneRef.current = { pause: () => { playing = false; ctx.close(); }, currentTime: 0 };
    } catch {}
  }, [stopRingtone]);

  const showNotification = useCallback((callerDisplay) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification("Incoming Call", {
        body: callerDisplay,
        icon: "/bonjour-logo-blue.png",
        requireInteraction: true,
        tag: "incoming-call",
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      notificationRef.current = n;
    }
  }, []);

  const dismissNotification = useCallback(() => {
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
    stopRingtone();
  }, [stopRingtone]);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Wire up call events
  const _bindCallEvents = useCallback((call, isIncoming = false) => {
    call.on("ringing", () => {
      // Don't override "incoming" status — we need it to show accept/reject buttons
      if (!isIncoming) setStatus("ringing");
    });

    call.on("accept", () => {
      setStatus("connected");
      setCallSid(call.parameters?.CallSid || null);
      startTimer();
    });

    call.on("disconnect", () => {
      setStatus("disconnected");
      stopTimer();
      callRef.current = null;
    });

    call.on("cancel", () => {
      setStatus("idle");
      stopTimer();
      callRef.current = null;
      setCallerInfo(null);
      setPhoneNumber("");
      dismissNotification();
    });

    call.on("error", (err) => {
      console.error("Twilio Call error:", err);
      const ui = formatErrorForUi(err);
      setError(ui);
      toast.error(`Call failed: ${ui.split("\n")[0]}`);
      reportToBackend("call_event", err, { phone_number: phoneNumber || null });
      setStatus("disconnected");
      stopTimer();
      callRef.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- phoneNumber is only read for the error report; depending on it would rebind every Twilio call handler on each dialer keystroke
  }, [startTimer, stopTimer, dismissNotification]);

  // Initialize device (once)
  const initDevice = useCallback(async () => {
    if (deviceRef.current || initializingRef.current) return;
    initializingRef.current = true;

    // Check the kill-switch / credential probe status before hitting
    // /voice/token. If the backend reports voice disabled (kill switch
    // off or startup probe failed), skip device init entirely so we
    // don't trigger 20101 AccessTokenInvalid errors on the client.
    try {
      const statusRes = await getTwilioVoiceStatus();
      const enabled = !!statusRes?.data?.enabled;
      setVoiceEnabled(enabled);
      setVoiceDisabledReason(statusRes?.data?.reason || null);
      if (!enabled) {
        console.info("Twilio Voice disabled by backend:", statusRes?.data?.reason);
        initializingRef.current = false;
        return;
      }
    } catch (statusErr) {
      // If the status endpoint itself fails, err on the side of showing
      // the button (so we don't hide voice when the outage is elsewhere)
      // but don't proceed with token init because token will 503 too.
      console.warn("Twilio Voice status check failed:", statusErr?.message || statusErr);
      setVoiceEnabled(true);
    }

    try {
      const res = await getVoiceToken();
      const token = res.data.token;

      const device = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        logLevel: "warn",
        edge: "dublin",
      });

      device.on("error", (err) => {
        console.error("Twilio Device error (initDevice):", err);
        reportToBackend("device_event", err);

        // Auto-recover from connection errors (stale WebSocket after idle tab).
        // Chrome suspends all network I/O for backgrounded tabs, so recovery
        // must be deferred until the tab becomes visible again.
        if (isConnectionError(err) && !recoveryAttemptedRef.current && !callRef.current) {
          try { device.destroy(); } catch {}
          deviceRef.current = null;
          setDeviceReady(false);
          initializingRef.current = false;

          if (document.visibilityState === "visible") {
            console.log("[twilio] Connection error while tab visible -- recovering now");
            recoveryAttemptedRef.current = true;
            setTimeout(() => { recoveryAttemptedRef.current = false; }, 60000);
            initDeviceRef.current?.();
          } else {
            console.log("[twilio] Connection error while tab backgrounded -- deferring recovery");
            needsRecoveryRef.current = true;
          }
          return;
        }

        const ui = formatErrorForUi(err);
        setError(ui);
        // Background device errors stay silent (no toast, no auto-opened
        // dialer). They are logged to the backend and shown in the dialer
        // when someone opens it. Only an active call gets a toast.
        if (callRef.current) toast.error(`Voice device error: ${ui.split("\n")[0]}`);
      });

      // Handle incoming calls
      device.on("incoming", async (call) => {
        callRef.current = call;
        const from = call.parameters?.From || "";
        setPhoneNumber(from);
        setStatus("incoming");
        setCallDirection("inbound");

        // Try to look up the caller
        let callerDisplay = from;
        try {
          const res = await lookupCaller(from);
          if (res.data.found) {
            setCallerInfo({ supplierId: res.data.supplier_id, supplierName: res.data.supplier_name });
            callerDisplay = `${res.data.supplier_name} (${from})`;
          } else {
            setCallerInfo(null);
          }
        } catch {
          setCallerInfo(null);
        }

        // Browser notification + ringtone
        showNotification(callerDisplay);
        startRingtone();

        _bindCallEvents(call, true);
      });

      device.on("tokenWillExpire", async () => {
        console.log("[twilio] tokenWillExpire fired — refreshing token...");
        try {
          const res = await getVoiceToken();
          device.updateToken(res.data.token);
          console.log("[twilio] Token refreshed successfully via tokenWillExpire");
        } catch (err) {
          console.error("[twilio] Token refresh failed (tokenWillExpire):", err);
          reportToBackend("token_refresh", err, { trigger: "tokenWillExpire" });
        }
      });

      // Save device immediately — outbound calls work without registration.
      // Registration is only needed for incoming calls.
      deviceRef.current = device;
      setDeviceReady(true);

      try {
        await device.register();
      } catch (regErr) {
        console.warn("Device registration failed (incoming calls won't work):", regErr?.message || regErr);
        // Always report the raw registration error so it lands in the
        // backend logs even if the diagnostics check below succeeds.
        reportToBackend("registration", regErr);
        // Run diagnostics to surface the real issue
        try {
          const diag = await checkVoiceConfig();
          const d = diag.data;
          const issues = [];
          if (d.twiml_voice_url && d.twiml_voice_url.includes("NOT SET")) issues.push("TwiML App Voice URL is not set");
          if (d.api_key && d.api_key.includes("INVALID")) issues.push("API Key is invalid");
          if (d.twiml_app && d.twiml_app.includes("INVALID")) issues.push("TwiML App SID is invalid");
          for (const [k, v] of Object.entries(d)) { if (v === "MISSING") issues.push(`${k} is missing`); }
          if (issues.length) {
            const issuesText = `Dialler setup issues:\n${issues.join("\n")}`;
            setError(issuesText);
            reportToBackend(
              "registration",
              { name: "RegistrationConfigIssues", message: issuesText },
              { extra: { issues, check_config: d } },
            );
          } else {
            console.log("Voice config check passed:", d);
          }
        } catch (diagErr) {
          // Diagnostic check itself failed. Still report the original
          // registration error so it's not swallowed.
          reportToBackend("registration", regErr, { extra: { check_config_failed: String(diagErr?.message || diagErr) } });
        }
      }
    } catch (err) {
      console.error("Voice device init failed:", err);
      const backendDetail = err?.response?.data?.detail;
      // Token endpoint failures have the most useful message in the
      // response body, not the JS Error.message
      const ui = backendDetail
        ? `[token_fetch] ${backendDetail}`
        : formatErrorForUi(err);
      setError(ui);
      // Silent on page load: logged to the backend, readable in the dialer.
      reportToBackend(
        backendDetail ? "token_fetch" : "device_init",
        err,
        { extra: backendDetail ? { backend_detail: backendDetail, http_status: err?.response?.status } : null },
      );
    } finally {
      initializingRef.current = false;
    }
  }, [_bindCallEvents, showNotification, startRingtone]);

  // Keep the ref in sync so error-recovery handlers always call the latest initDevice.
  useEffect(() => { initDeviceRef.current = initDevice; }, [initDevice]);

  // Init on mount
  useEffect(() => {
    initDevice();

    // When the tab becomes visible again, recover the Twilio Device if it
    // was destroyed while backgrounded (Chrome suspends network I/O for
    // idle tabs, so recovery must wait until the tab is foregrounded).
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && needsRecoveryRef.current) {
        console.log("[twilio] Tab became visible -- executing deferred recovery");
        needsRecoveryRef.current = false;
        recoveryAttemptedRef.current = true;
        setTimeout(() => { recoveryAttemptedRef.current = false; }, 60000);
        initDeviceRef.current?.();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Fallback: refresh the token every 50 minutes in case the SDK's
    // tokenWillExpire event doesn't fire in a backgrounded/idle tab.
    // The backend token TTL is 3600s (1 hour).
    const TOKEN_REFRESH_MS = 50 * 60 * 1000;
    tokenRefreshIntervalRef.current = setInterval(async () => {
      if (!deviceRef.current) return;
      try {
        console.log("[twilio] Interval token refresh (fallback)...");
        const res = await getVoiceToken();
        if (deviceRef.current) {
          deviceRef.current.updateToken(res.data.token);
          console.log("[twilio] Token refreshed via interval fallback");
        }
      } catch (err) {
        console.error("[twilio] Interval token refresh failed:", err);
        reportToBackend("token_refresh", err, { trigger: "interval_fallback" });
      }
    }, TOKEN_REFRESH_MS);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timerRef.current) clearInterval(timerRef.current);
      if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch {}
        deviceRef.current = null;
      }
      dismissNotification();
    };
  }, [initDevice, dismissNotification]);

  // Helper: wrap a promise with a timeout
  const withTimeout = (promise, ms, label) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms)),
    ]);

  // Make an outbound call
  const makeCall = useCallback(async (number, info = {}) => {
    // Short-circuit when the backend kill switch is off. Show a clear
    // message instead of calling /voice/token (which would 503) and
    // then handing a bad token to the Voice SDK (which would surface as
    // a confusing 20101 AccessTokenInvalid).
    if (voiceEnabled === false) {
      const msg = `Voice calling is disabled: ${voiceDisabledReason || "credentials not configured"}`;
      setError(msg);
      toast.error(msg);
      return;
    }
    setError(null);
    setPhoneNumber(number);
    setCallerInfo(info);
    setCallDuration(0);
    setIsMuted(false);
    setShowDialer(false);
    setCallDirection("outbound");

    // Ensure device exists
    if (!deviceRef.current) {
      setStatus("initializing");
      try {
        const res = await withTimeout(getVoiceToken(), 10000, "Token generation");
        const token = res.data.token;
        const device = new Device(token, { codecPreferences: ["opus", "pcmu"], logLevel: "warn", edge: "dublin" });

        // Listen for registration errors
        device.on("error", (err) => {
          console.error("Twilio Device error (makeCall init):", err);
          reportToBackend("device_event", err, { phone_number: number });

          // Auto-recover from connection errors (stale WebSocket after idle)
          if (isConnectionError(err) && deviceRef.current === device && !callRef.current) {
            console.log("[twilio] Connection error after idle -- destroying stale Device");
            try { device.destroy(); } catch {}
            deviceRef.current = null;
            setDeviceReady(false);
            initializingRef.current = false;
            if (document.visibilityState !== "visible") {
              needsRecoveryRef.current = true;
            }
            return;
          }

          const ui = formatErrorForUi(err);
          setError(ui);
          if (callRef.current) toast.error(`Voice device error: ${ui.split("\n")[0]}`);
          else setStatus("disconnected");
        });

        // Mirror the refresh handler from initDevice so the fallback
        // device path is also structurally immune to 20104
        // AccessTokenExpired. Without this, a device lazily created
        // here would never refresh its token, and a long-lived tab
        // that took this code path would eventually fail its next call.
        device.on("tokenWillExpire", async () => {
          console.log("[twilio] tokenWillExpire fired — refreshing token...");
          try {
            const freshRes = await getVoiceToken();
            device.updateToken(freshRes.data.token);
            console.log("[twilio] Token refreshed successfully via tokenWillExpire");
          } catch (err) {
            console.error("[twilio] Token refresh failed (tokenWillExpire):", err);
            reportToBackend("token_refresh", err, { trigger: "tokenWillExpire" });
          }
        });

        deviceRef.current = device;
        setDeviceReady(true);
        // Registration is for incoming calls — don't block outbound on it
        device.register().catch((regErr) => {
          console.warn("Device registration failed (incoming calls won't work):", regErr?.message || regErr);
          reportToBackend("registration", regErr, { phone_number: number });
        });
      } catch (err) {
        console.error("Voice init failed (makeCall):", err);
        const backendDetail = err?.response?.data?.detail;
        const ui = backendDetail
          ? `[token_fetch] ${backendDetail}`
          : formatErrorForUi(err);
        setError(ui);
        toast.error(`Voice setup failed: ${(backendDetail || err?.message || "init failed").slice(0, 200)}`);
        reportToBackend(
          backendDetail ? "token_fetch" : "device_init",
          err,
          { phone_number: number, extra: backendDetail ? { backend_detail: backendDetail, http_status: err?.response?.status } : null },
        );
        setStatus("disconnected");
        setShowDialer(true);
        return;
      }
    }

    setStatus("connecting");

    try {
      const call = await deviceRef.current.connect({ params: { To: number } });
      callRef.current = call;
      _bindCallEvents(call);

      // Safety timeout: if no ringing/accept within 30s, something is wrong
      const connectTimeout = setTimeout(() => {
        if (callRef.current === call && !["ringing", "connected"].includes(statusRef.current)) {
          const msg = "Call failed — no response from network. Check Twilio TwiML App Voice URL is set and points to /api/voice/dial.";
          setError(msg);
          toast.error("Call timed out (no network response)");
          reportToBackend(
            "connect",
            { name: "ConnectTimeout", message: msg },
            { phone_number: number, extra: { last_status: statusRef.current } },
          );
          try { call.disconnect(); } catch {}
          callRef.current = null;
          setStatus("disconnected");
          setShowDialer(true);
        }
      }, 30000);

      // Clear timeout once we get a real event
      call.on("ringing", () => clearTimeout(connectTimeout));
      call.on("accept", () => clearTimeout(connectTimeout));
      call.on("disconnect", () => clearTimeout(connectTimeout));
      call.on("error", () => clearTimeout(connectTimeout));
      call.on("cancel", () => clearTimeout(connectTimeout));
    } catch (err) {
      console.error("Call connect failed:", err);

      // Connection error from stale Device after idle. The Device error
      // handler may have already destroyed it; clean up if not.
      if (isConnectionError(err) && deviceRef.current && !callRef.current) {
        console.log("[twilio] Connect failed with connection error — destroying stale Device");
        try { deviceRef.current.destroy(); } catch {}
        deviceRef.current = null;
        setDeviceReady(false);
        initializingRef.current = false;
      }

      const ui = formatErrorForUi(err);
      setError(ui);
      toast.error(
        isConnectionError(err)
          ? "Connection lost after idle — please try calling again"
          : `Call failed: ${ui.split("\n")[0]}`
      );
      reportToBackend("connect", err, { phone_number: number });
      setStatus("disconnected");
      setShowDialer(true);
    }
  }, [_bindCallEvents, voiceEnabled, voiceDisabledReason]);

  // Accept incoming call
  const acceptCall = useCallback(() => {
    if (callRef.current && status === "incoming") {
      callRef.current.accept();
      dismissNotification();
    }
  }, [status, dismissNotification]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (callRef.current && status === "incoming") {
      callRef.current.reject();
      callRef.current = null;
      setStatus("idle");
      setCallerInfo(null);
      setPhoneNumber("");
      dismissNotification();
    }
  }, [status, dismissNotification]);

  const hangUp = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    } else {
      // No active call (e.g. failed before connecting) — reset to idle
      setStatus("disconnected");
    }
    stopTimer();
  }, [stopTimer]);

  const sendDigit = useCallback((digit) => {
    if (callRef.current) callRef.current.sendDigits(digit);
  }, []);

  const toggleMute = useCallback(() => {
    if (callRef.current) {
      const m = !callRef.current.isMuted();
      callRef.current.mute(m);
      setIsMuted(m);
    }
  }, []);

  // Log call and reset
  const endAndLog = useCallback(async () => {
    // Disconnect any active call first
    if (callRef.current) {
      try { callRef.current.disconnect(); } catch {}
      callRef.current = null;
    }
    if (callDuration > 0 || status === "disconnected") {
      try {
        await logVoiceCall({
          supplier_id: callerInfo?.supplierId || "",
          enquiry_id: callerInfo?.enquiryId || "",
          phone_number: phoneNumber,
          call_sid: callSid || "",
          duration_seconds: callDuration,
          direction: callDirection,
        });
      } catch {}
    }
    setStatus("idle");
    setError(null);
    setCallDuration(0);
    setIsMuted(false);
    setCallSid(null);
    setPhoneNumber("");
    setCallerInfo(null);
    setCallDirection("outbound");
  }, [callDuration, callSid, callerInfo, phoneNumber, status, callDirection]);

  return (
    <VoiceContext.Provider value={{
      status, isMuted, callDuration, callSid, error, phoneNumber, callerInfo,
      showDialer, setShowDialer, deviceReady, callDirection,
      voiceEnabled, voiceDisabledReason,
      makeCall, hangUp, acceptCall, rejectCall, sendDigit, toggleMute, endAndLog,
      setPhoneNumber, setCallerInfo,
      clearError: () => setError(null),
    }}>
      {children}
    </VoiceContext.Provider>
  );
}
